import { ContractType, ContractMode, TagFilterMode } from '@/utils/Enumerations.js'
import { theme } from '@/plugins/theme'
import store from '@/store/index.js'
import * as vuetify from '@/plugins/vuetify'
import { LocalStorageKeys } from '@/utils/LocalStorageActor'
import { MOBILE_BREAKPOINT } from '@/utils/DeviceUtility'
import { sumBy } from 'lodash'

const STANDARD_BROWSER_SCROLLBAR_SIZE = 15

function bp () {
  return vuetify.default.framework.breakpoint
}

export const selectBreakpoint = (values, breakpoint) => {
  let currentBp = values.xs ?? values.sm ?? values.md ?? values.lg ?? values.xl
  for (const bp of ['xs', 'sm', 'md', 'lg', 'xl']) {
    currentBp = values[bp] ? values[bp] : currentBp
    if (bp === breakpoint) {
      return currentBp
    }
  }
}

export async function tickTock () {
  for (const fn of arguments) {
    await this.$nextTick()
    fn()
  }
}

export const selectorSelectionTextStyle = {
  color: '#495057',
  fontSize: '14px'
}

export const uniqueDialogId = tag => `${tag}-${Math.floor(Math.random() * 0xFFFFFF).toString(16)}` // TODO MK: Refactor for the name "uniqueId" - doesn't need to be for dialogs

export const contractModeFor = (contractTypeValue) => {
  const contractType = ContractType.forInt(contractTypeValue)
  switch (contractType) {
    case ContractType.Production:
    case ContractType.WoodsSale:
      return ContractMode.Logs
    case ContractType.ByproductSale:
    case ContractType.ByproductPurchase:
    case ContractType.ByproductHauling:
      return ContractMode.Byproducts
    case ContractType.Transfer:
      return ContractMode.Transfers
    case ContractType.LogYardSale:
      return ContractMode.LogYardSale
  }
}

// Contract types whose tickets carry a departure weight
export const contractTypeHasDepartureWeight = (contractTypeValue) => [
  ContractType.ByproductSale.value,
  ContractType.LogYardSale.value,
  ContractType.ByproductHauling.value,
  ContractType.Transfer.value
].includes(contractTypeValue)

// Contract types that produce a gate pass QR code
export const contractTypeHasGatePass = (contractTypeValue) => [
  ContractType.ByproductSale.value,
  ContractType.LogYardSale.value,
  ContractType.Transfer.value
].includes(contractTypeValue)

export const colorClassForContractType = (contractType, includeWhiteText = true) => {
  const whiteTextString = includeWhiteText ? ' white--text' : ''
  let color = ''
  switch (contractType) {
    case ContractType.ByproductSale.value:
    case ContractType.ByproductPurchase.value:
    case ContractType.ByproductHauling.value:
      color = 'byproduct'
      break
    case ContractType.Transfer.value:
      color = 'transfer'
      break
    case ContractType.LogYardSale.value:
      color = 'logyardsale'
      break
    case ContractType.Production.value:
    case ContractType.WoodsSale.value:
    default:
      color = 'primary'
  }

  return `${color} ${whiteTextString}`
}

export const colorClassForContractMode = (contractMode) => {
  return `${ContractMode.forInt(contractMode).details.color} white--text`
}

export const contractKeyLabel = ({ type, tract, setting, fromAccount, destinationAccount, account }) => {
  const keys = [tract, fromAccount, destinationAccount, setting, account].filter(Boolean)
  return `${ContractType.fromInt(type)}: ${keys.join(', ')}`
}

export const hexCodeForContractType = (contractType) => {
  switch (contractType) {
    case ContractType.ByproductPurchase.value:
    case ContractType.ByproductSale.value:
      return theme.byproduct
    case ContractType.Transfer.value:
      return theme.transfer
    case ContractType.LogYardSale.value:
      return theme.logyardsale
    case ContractType.Production.value:
    case ContractType.WoodsSale.value:
    default:
      return theme.primary
  }
}

export const hexCodeForContractMode = (contractMode) => {
  return theme[ContractMode.forInt(contractMode).details.color]
}

export const getMenuContainerStyle = (w, h) => {
  const maxHeight = h - 200
  const maxWidth = w - 100
  const minWidth = w > 500 ? 400 : w - 100

  const styleObject = w > 550 && h > 800
    ? {
      width: '400px'
    }
    : {
      display: 'flex',
      flexDirection: 'column',
      overflow: 'auto',
      maxHeight: `${maxHeight}px`,
      maxWidth: `${maxWidth}px`,
      minWidth: `${minWidth}px`
    }
  return styleObject
}

export const autocompleteFilterChipStyle = {
  position: 'relative',
  right: '-115px',
  top: '24px',
  minHeight: '20px',
  marginTop: '-20px',
  maxWidth: 'calc(100% - 165px)',
  display: 'flex',
  justifyContent: 'flex-end',
  flexWrap: 'wrap',
  zIndex: 1
}

export const accountMatchesTagFilter = (accountTagValues, selectedTags, mode) => {
  if (selectedTags.length === 0) return true
  const hasTag = tag => accountTagValues.includes(tag.value)
  return mode === TagFilterMode.All.value ? selectedTags.every(hasTag) : selectedTags.some(hasTag)
}

export const dataTableMinItemsPerPage = 10
export const dataTableDefaultItemsPerPage = 25
export const dataTableDefaultItemsPerPage4k = 50
export const dataTableItemsPerPage = [
  dataTableMinItemsPerPage,
  dataTableDefaultItemsPerPage,
  dataTableDefaultItemsPerPage4k,
  100
]

export const dataTableItemsPerPageIncludeAll = [
  ...dataTableItemsPerPage,
  -1
]

const FOUR_K_MONITOR_HEIGHT_APPROX = 2000
export function getDataTableDefaultItemsPerPage (vuetifyBreakpointObject) {
  const { height } = vuetifyBreakpointObject
  return height > FOUR_K_MONITOR_HEIGHT_APPROX ? dataTableDefaultItemsPerPage4k : dataTableDefaultItemsPerPage
}

export const RowHeights = {
  Dense: {
    timesWrapped: (wrapTimes) => {
      const zero = 32
      const one = 43
      const perWrap = 21
      switch (wrapTimes) {
        case 0:
          return zero
        case 1:
          return one
        default:
          return one + (perWrap * (wrapTimes - 1))
      }
    }
  }
}

export const AUTO_HEIGHT_DATA_SIZE = 8
export const FINANCIAL_PANEL_TABLE_MAX_HEIGHT = 300

const DIALOG_VIEW_PERCENT = 90
const FULLSCREEN_VIEW_PERCENT = 100
export function cssViewHeightForDialogWithFullscreenState (isFullscreen) {
  return `${isFullscreen ? FULLSCREEN_VIEW_PERCENT : DIALOG_VIEW_PERCENT}vh`
}

export const DATA_TABLE_HEADER_HEIGHT = 32
export const DATA_TABLE_FOOTER_HEIGHT = 59

const numDataTablesOnScreen = () => {
  const t = document.querySelectorAll('.v-data-table')
  return t.length
}

const oneTableOnScreen = () => numDataTablesOnScreen() === 1

const getTableWrapperQuery = (id = null) => id
  ? `div.v-data-table#${id}>div.v-data-table__wrapper`
  : 'div.v-data-table>div.v-data-table__wrapper'
const getAllRowsQuery = (id = null) => id
  ? `div.v-data-table#${id}>div.v-data-table__wrapper>table>tbody>tr`
  : 'div.v-data-table>div.v-data-table__wrapper>table>tbody>tr'
const getHeaderQuery = (id = null) => id
  ? `div.v-data-table#${id}>div.v-data-table__wrapper>table>thead.v-data-table-header>tr`
  : 'div.v-data-table>div.v-data-table__wrapper>table>thead.v-data-table-header>tr'
const getFooterQuery = (id = null) => id
  ? `div.v-data-table#${id}>div.v-data-footer`
  : 'div.v-data-table>div.v-data-footer'

const getHeightOfDataInTable = (id = null) => {
  if (!id && !oneTableOnScreen()) return undefined
  const rows = document.querySelectorAll(getAllRowsQuery(id))
  if (!rows || rows.length === 0) return undefined

  const header = document.querySelector(getHeaderQuery(id))
  if (!header) return undefined

  const wrapper = document.querySelector(getTableWrapperQuery(id))
  let scrollbarHeight = 0
  if (wrapper) {
    const { scrollWidth, offsetWidth } = wrapper
    scrollbarHeight += scrollWidth > offsetWidth ? STANDARD_BROWSER_SCROLLBAR_SIZE : 0
  }
  const height = sumBy(rows, row => row.scrollHeight) + header.scrollHeight + scrollbarHeight /* scrollHeight is height including potential overflow */
  return height
}

const drawerMini = () => localStorage.getItem(LocalStorageKeys.DRAWER_MINI) === 'true'

export const autoHeightCalculationDividend = () => {
  const breakpoint = bp().name
  if (bp().width < MOBILE_BREAKPOINT) {
    switch (breakpoint) {
      case 'md': return 200
      case 'sm': return 300
    }
  } else {
    if (drawerMini()) {
      switch (breakpoint) {
        case 'xl': return 150
        case 'lg': return 200
        case 'md': return 200
      }
    } else {
      switch (breakpoint) {
        case 'xl': return 160
        case 'lg': return 200
        case 'md': return 200
      }
    }
  }
}

export const thresholdForAutoHeightByWindowHeight = () => {
  return drawerMini() ? 700 : 800
}

function tableAutoHeightThreshold () { return Math.ceil(getAdjustedHeight() / autoHeightCalculationDividend()) }

function getAdjustedHeight (tableElementId = null) {
  return getIsFullscreen(tableElementId)
    ? bp().height
    : DIALOG_VIEW_PERCENT / FULLSCREEN_VIEW_PERCENT * bp().height
}

export function tableHeight ({
  dataLength,
  offset, // Height to be subtracted because it is occupied by other content
  tableElementId = null,
  headersLength = null,
  footerVisible = null,
  absoluteMinimum = 256,
  absoluteMaximum = null,
  avgColumnWidth = 128
}) {
  if (shouldReturnAuto(tableElementId)) return 'auto'

  const tableHeightActualResult = tableHeightActual(offset, tableElementId, footerVisible)
  if (tableHeightActualResult != null) return tableHeightActualResult

  if (shouldReturnAutoIfLackingActualDataHeight(dataLength)) return 'auto'

  const expectedWrapTimes = getExpectedWrapTimes(headersLength, avgColumnWidth)

  absoluteMinimum = RowHeights.Dense.timesWrapped(0) * tableAutoHeightThreshold()

  if (footerVisible == null && (!!tableElementId || oneTableOnScreen())) {
    footerVisible = !!document.querySelector(getFooterQuery(tableElementId))
  }

  if (footerVisible) offset += DATA_TABLE_FOOTER_HEIGHT

  const height = getHeightCalculationString(
    absoluteMinimum,
    absoluteMaximum,
    getExpectedDataHeight(dataLength, expectedWrapTimes),
    getViewportMaximum(offset, tableElementId))
  return height
}

function shouldReturnAuto (tableElementId = null) {
  return bp().xsOnly || getAdjustedHeight(tableElementId) < thresholdForAutoHeightByWindowHeight()
}

function shouldReturnAutoIfLackingActualDataHeight (dataLength) {
  return dataLength < tableAutoHeightThreshold()
}

function getViewportMaximum (offset, tableElementId = null) {
  return `calc(${cssViewHeightForDialogWithFullscreenState(getIsFullscreen(tableElementId))} - ${offset}px)`
}

function getIsFullscreen (tableElementId = null) {
  if (tableElementId) {
    const tableAsDialogDescendant = document.querySelector(`.v-dialog #${tableElementId}`)
    const tableIsWithinDialog = !!tableAsDialogDescendant
    if (!tableIsWithinDialog) return true
  }
  const topDialogState = store.getters['dialog/topDialogState']
  const automaticFullscreen = !!topDialogState && bp().mdAndDown
  return (topDialogState?.fullscreen || automaticFullscreen || !topDialogState)
}

function getExpectedDataHeight (dataLength, expectedWrapTimes) {
  return DATA_TABLE_HEADER_HEIGHT + (RowHeights.Dense.timesWrapped(expectedWrapTimes) * dataLength)
}

function getHeightCalculationString (absoluteMinimum, absoluteMaximum, expectedDataHeight, viewportMaximum) {
  let height = `min(${expectedDataHeight}px, ${viewportMaximum})`
  height = `max(${absoluteMinimum}px, ${height})`
  if (absoluteMaximum != null) height = `min(${absoluteMaximum}px, ${height})`
  return height
}

function getExpectedWrapTimes (headersLength, avgColumnWidth) {
  if (headersLength === null) {
    headersLength = 10
  }
  const expectedWidth = headersLength * avgColumnWidth
  const viewportWidth = bp().width
  const result = Math.floor(expectedWidth / viewportWidth)
  return result
}

export function tableHeightActual (offset, tableElementId = null, footerVisible = false, absoluteMinimum = null) {
  if (shouldReturnAuto(tableElementId)) return 'auto'
  if (footerVisible) offset += DATA_TABLE_FOOTER_HEIGHT
  const heightOfData = getHeightOfDataInTable(tableElementId)
  if (!heightOfData) return 'auto'
  let result = `min(${heightOfData}px, ${getViewportMaximum(offset, tableElementId)})`
  if (absoluteMinimum) result = `max(${absoluteMinimum}px, ${result})`
  return result
}
